import "../CourseDetails.css";
import "./PackDetails.css";
import { useEffect, useState } from "react";
import axios from "axios";
import { Button, Skeleton } from "@mantine/core";
import { IconBooks, IconReport, IconCertificate, IconDiscount2 } from "@tabler/icons-react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout/MainLayout";
import CourseHero from "../../features/Course/CourseHero/CourseHero";
import CourseDetailsCard from "../../features/Course/CourseDetailsCard/CourseDetailsCard";
import CourseRequirements from "../../features/Course/CourseRequirements/CourseRequirements";
import CourseBenefits from "../../features/Course/CourseBenefits/CourseBenefits";
import CardInfoColumn from "../../components/CardInfo/CardInfoColumn/CardInfoColumn";
import SpecialtiesCardsContainer from "../../features/Specialties/SpecialtiesCardsContainer/SpecialtiesCardsContainer";
import DecorativeCircle from "../../components/DecorativeCircle/DecorativeCircle";
import TinyDecorativeCircle from "../../components/TinyDecorativeCircle/TinyDecorativeCircle";
import { useAppContext } from "../../context/AppState/AppContext";
import { calculatePrice } from "../../utils/calculatePrice";
import { formattedPrice } from "../../utils/formatted-price";
import { currencyFormat } from "../../utils/currencyFormat";
import { formatCourseStartDate } from "../../utils/courses-dates";
import { ROUTES_PATHNAME } from "../../routes/dict";

const featuresBuilder = ({ coursesCount, totalClasses, separatelyPrice, savings, isUSD }) => {
    const features = [
        {
            icon: <IconBooks className="cardFeature__icon" />,
            text: "Cursos incluidos",
            description: `${coursesCount} cursos completos`
        },
        {
            icon: <IconReport className="cardFeature__icon" />,
            text: "Duración total",
            description: totalClasses ? `${totalClasses} clases` : "A confirmar"
        },
        {
            icon: <IconCertificate className="cardFeature__icon" />,
            text: "Certificación",
            description: "Un certificado por cada curso"
        },
    ];
    if (savings > 0) {
        features.push({
            icon: <IconDiscount2 className="cardFeature__icon" />,
            text: "Ahorrás",
            description: `${formattedPrice(currencyFormat(savings), isUSD)} (por separado: ${formattedPrice(currencyFormat(separatelyPrice), isUSD)})`
        });
    }
    return features;
};

export default function PackDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { setPaymentData, isUSD, isMobile, officialUSDValue } = useAppContext();
    const [pack, setPack] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setPack(null);
        setNotFound(false);
        axios.get(`${process.env.REACT_APP_BACKEND_SERVER}/info/packs/${id}`)
            .then(response => setPack(response.data))
            .catch(err => {
                console.log(err);
                setNotFound(true);
            });
    }, [id]);

    if (notFound) {
        return (
            <MainLayout>
                <section className="course__section">
                    <span className="sectionTitle-upText">No encontramos este pack</span>
                </section>
            </MainLayout>
        );
    }

    if (!pack) return <MainLayout><Skeleton height={400} radius="sm" /></MainLayout>;

    const info = pack.comercialInformation || {};
    const academicUnits = pack.academicUnits || [];
    const isOpen = !!pack.onSale;
    const price = calculatePrice(info.price, officialUSDValue, isUSD, pack.type);
    const separatelyPrice = pack.priceComparison?.separately
        ? calculatePrice(pack.priceComparison.separately, officialUSDValue, isUSD, pack.type)
        : 0;
    const savings = Math.max(separatelyPrice - price, 0);
    const totalClasses = academicUnits.reduce((acc, au) => acc + (au.comercialInformation?.classQuantity || 0), 0);

    const handleInscription = () => {
        if (!isOpen) return;
        setPaymentData({
            name: info.name,
            id: pack._id,
            price,
            image: info.image,
            division: pack.division || null,
            subscriptionPlans: info.subscriptionPlans || [],
            isPack: true,
        });
        navigate("/pago");
    };

    // Para "Consultar un asesor": el formulario de contacto lo informa como pack
    const courseData = { name: info.name, id: pack._id, isPack: true };

    return (
        <>
            <Helmet>
                <title>{info.name} | Forvet</title>
                <meta name="description" content={info.description?.slice(0, 160)} />
                <meta property="og:title" content={info.name} />
                <meta property="og:description" content={info.description} />
                <meta property="og:image" content={info.heroImage || info.image} />
                <meta property="og:url" content={`https://forvet.org/packs/${pack._id}`} />
            </Helmet>
            <MainLayout>
                <div className="course__layout">
                    <section className="course__hero" style={{ position: "relative" }}>
                        <CourseHero
                            isOpen={isOpen}
                            title={info.name}
                            description={info.description}
                            imageUrl={info.heroImage || info.image}
                        />
                        <DecorativeCircle size={100} top={-20} left={0} opacity={.1} shadow={1} />
                        <DecorativeCircle size={150} bottom={-100} left={300} opacity={.1} shadow={1} hideInMobile />
                        <DecorativeCircle size={100} top={-50} right={0} opacity={.1} shadow={2} />
                        <TinyDecorativeCircle size={12} top={50} right={300} opacity={.1} color={1} />
                        <TinyDecorativeCircle size={32} top={80} left={500} opacity={.1} color={3} hideInMobile />
                    </section>

                    <aside className="course__aside">
                        <CourseDetailsCard
                            price={price}
                            videoUrl={info.video}
                            features={featuresBuilder({ coursesCount: academicUnits.length, totalClasses, separatelyPrice, savings, isUSD })}
                            handleInscription={handleInscription}
                            isOpen={isOpen}
                            courseData={courseData}
                        />
                    </aside>

                    <div className="course__main">
                        <section className="course__section packDetails__includes">
                            <SpecialtiesCardsContainer sectionTitle="Qué incluye este pack">
                                {academicUnits.map(au => {
                                    const auInfo = au.comercialInformation || {};
                                    return (
                                        <CardInfoColumn
                                            key={au._id}
                                            imgUrl={auInfo.image}
                                            type={au.type}
                                            title={auInfo.name?.toLowerCase()}
                                            price={calculatePrice(auInfo.price, officialUSDValue, isUSD, au.type)}
                                            booleanValue
                                            classes={auInfo.classQuantity || "A confirmar"}
                                            date={formatCourseStartDate(au)}
                                            buttonText="Ver curso"
                                            buttonColor="var(--color-primary)"
                                            buttonAction={() => navigate(`${ROUTES_PATHNAME.courses.href}/${au._id}`)}
                                        />
                                    );
                                })}
                            </SpecialtiesCardsContainer>
                        </section>

                        <section className="course__section packDetails__note">
                            <p>
                                Con el pack quedás inscripto en {academicUnits.length === 1 ? "el curso" : `los ${academicUnits.length} cursos`} y
                                los cursás en el campus como cualquier alumno. Al aprobar cada uno, recibís su certificado.
                            </p>
                        </section>

                        {info.whoIsThisFor &&
                            <section className="course__section--noPadding">
                                <CourseRequirements text={info.whoIsThisFor} />
                            </section>}

                        <section className="course__section">
                            <CourseBenefits />
                        </section>

                        {!isOpen && !isMobile &&
                            <section className="course__section">
                                <span className="sectionTitle-upText">¡Inscripciones cerradas!</span>
                            </section>}
                    </div>
                </div>

                <div className="course__buttons">
                    <Button className="courseDetailsCard__button" fw="normal" color="secondaryColor" disabled={!isOpen}
                        fullWidth onClick={handleInscription}>
                        {isOpen ? "Inscribirse" : "Inscripciones cerradas"}
                    </Button>
                    <Button className="courseDetailsCard__button" fw="normal" variant="default" fullWidth
                        onClick={() => navigate("/contacto", { state: courseData })}>
                        Consultar un asesor
                    </Button>
                </div>
            </MainLayout>
        </>
    );
}
